/**
 * Composant - AccountSearchStep
 *
 * Étape de recherche de compte pour versement.
 * Appelle le CBS via cbsAccountLookup, affiche les infos client (sans solde),
 * et permet de confirmer pour passer au billetage.
 */

import React, { useState } from 'react';
import { AlertCircle, ArrowRight, CheckCircle, Loader2, Lock, Search } from 'lucide-react';
import { CbsAccountInfo } from '../../../application/ports/output/ICbsAccountLookup';
import '../withdrawal/etapes.css';

interface AccountSearchStepProps {
  onAccountConfirmed: (info: CbsAccountInfo) => void;
  onSearch: (numcpt: string) => Promise<CbsAccountInfo | null>;
  loading?: boolean;
}

const LONGUEUR_MIN = 10;

export function AccountSearchStep({ onAccountConfirmed, onSearch, loading }: AccountSearchStepProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<CbsAccountInfo | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim() || searching) return;

    setSearchResult(null);
    setSearchError(null);
    setSearching(true);

    try {
      const result = await onSearch(searchQuery.trim());
      if (result) {
        setSearchResult(result);
      } else {
        setSearchError('Aucun compte trouvé pour ce numéro.');
      }
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Erreur lors de la recherche du compte.');
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = () => {
    if (searchResult) {
      onAccountConfirmed(searchResult);
    }
  };

  const isSearching = searching || loading;
  const rechercheImpossible = isSearching || searchQuery.trim().length < LONGUEUR_MIN;

  return (
    <div className="et">
      <section className="et-carte">
        <header className="et-entete">
          <span className="ico">
            <Search />
          </span>
          <div>
            <h2>Compte à créditer</h2>
            <p>Saisissez le numéro de compte du client, puis vérifiez le titulaire.</p>
          </div>
        </header>

        <div className="et-corps">
          <form onSubmit={handleSearch} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="et-champ">
              <label htmlFor="numero-compte">
                Numéro de compte <span className="req">*</span>
              </label>
              <input
                id="numero-compte"
                className="et-input grand"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                value={searchQuery}
                onChange={(e) => {
                  // N'accepter que les chiffres ; un nouveau numéro efface le résultat précédent
                  setSearchQuery(e.target.value.replace(/\D/g, ''));
                  setSearchResult(null);
                  setSearchError(null);
                }}
                maxLength={20}
                required
                disabled={isSearching}
              />
              <span className="et-compteur">
                {searchQuery.length < LONGUEUR_MIN
                  ? `${LONGUEUR_MIN} chiffres minimum`
                  : `${searchQuery.length} chiffres`}
              </span>
            </div>

            <button type="submit" disabled={rechercheImpossible} className="et-btn principal">
              {isSearching ? (
                <>
                  <Loader2 className="et-tourne" /> Recherche en cours…
                </>
              ) : (
                <>
                  <Search /> Rechercher le compte
                </>
              )}
            </button>
          </form>

          {searchError && (
            <div className="et-note erreur" role="alert">
              <AlertCircle />
              <p>
                <strong>Compte introuvable</strong>
                {searchError}
              </p>
            </div>
          )}

          {searchResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p className="et-section-titre">
                <span>
                  <CheckCircle /> Compte trouvé
                </span>
              </p>
              <div className="et-client">
                <span className="et-avatar">{initiales(searchResult.intitule)}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p className="nom">{searchResult.intitule}</p>
                  <p className="sous">
                    Compte <span className="et-mono">{searchResult.numcpt}</span>
                    {searchResult.codeuser && (
                      <>
                        {' '}
                        · Code client <span className="et-mono">{searchResult.codeuser}</span>
                      </>
                    )}
                  </p>
                </div>
                <span className="et-pastille vert">
                  <CheckCircle /> Vérifié au CBS
                </span>
              </div>
              <button type="button" onClick={handleConfirm} className="et-btn principal">
                Continuer vers le billetage
                <ArrowRight />
              </button>
            </div>
          )}
        </div>

        <p className="et-pied">
          <Lock />
          Le titulaire est vérifié au CBS avant toute saisie : confirmez-le avec le déposant.
        </p>
      </section>
    </div>
  );
}

function initiales(nom?: string) {
  return (
    (nom ?? '?')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((m) => m[0]?.toUpperCase())
      .join('') || '?'
  );
}
